<template>
  <!--
    «Запрос суммы» in the chat (docs/cabinet-redesign/07 §4.3, §4.10): one card in each side's thread, the same state for
    both, read from the poll. The payer reads «Анна Иванова просит» with «Разрешить» (a smaller amount allowed, the
    master address that pays, then a step-up) and «Отклонить»; the requester reads «Ваш запрос · Иван Петров» with
    «Отменить». The card says who asks and to which card (§4.9 Н11) and warns about a card issued in the last 24 hours.
  -->
  <div class="cb mr" :class="{ 'cb--muted': closed }" data-testid="chat-block-MONEY_REQUEST"
       :data-state="r?.state" :data-side="r?.side">
    <template v-if="r">
      <div class="mr__head">
        <span class="mr__title" data-testid="money-request-title">{{ lineText(title!) }}</span>
        <DsStatusChip :tone="requestTone(r.state)" :label="t(requestStateKey(r))" data-testid="money-request-state" />
      </div>
      <div class="mr__amount num" data-testid="money-request-amount">{{ token(r.amount, r.asset) }} <small>{{ r.asset }}</small></div>
      <p v-if="partial" class="cb__hint" data-testid="money-request-approved">
        {{ t('cabinet.money.approvedOf', { approved: token(r.approvedAmount!, r.asset), amount: token(r.amount, r.asset),
                                           asset: r.asset }) }}
      </p>
      <p v-if="r.note" class="mr__note" data-testid="money-request-note">«{{ r.note }}»</p>

      <div class="cb__row">
        <small>{{ t('cabinet.money.toCard') }}</small>
        <span data-testid="money-request-card">{{ cardText }}</span>
      </div>
      <div v-if="r.state === 'PENDING'" class="cb__row">
        <small>{{ t('cabinet.money.until') }}</small>
        <span data-testid="money-request-expires">{{ dateTime(r.expiresAt) }}</span>
      </div>
      <div v-if="r.card.issuedRecently && r.side === 'PAYER' && r.state === 'PENDING'" class="cb__notice"
           data-testid="money-request-new-card">
        <div class="cb__noticeTitle">{{ t('cabinet.money.newCardTitle') }}</div>
        <div class="cb__noticeBody">{{ t('cabinet.money.newCardBody', { name: other.name ?? t('cabinet.money.someone') }) }}</div>
      </div>
      <p v-if="r.state === 'FAILED'" class="cb__hint" data-testid="money-request-failed">{{ t('cabinet.money.failedNote') }}</p>
      <!-- Why it did not reach the card — the same for both sides («Участник вышел из семьи», E2E family office Б-3). -->
      <p v-if="failureReasonKey" class="mr__reason" data-testid="money-request-failed-reason">{{ t(failureReasonKey) }}</p>

      <!-- §4.3 item 3: less on the shared balance than asked — say how much there is and offer that much, or the top-up. -->
      <p v-if="buttons.approve && (plan.kind === 'partial' || plan.kind === 'none')" class="cb__hint"
         data-testid="money-request-short">
        {{ t(plan.kind === 'none' ? 'cabinet.money.noBalance' : 'cabinet.money.shortBalance',
             { available: token(plan.available, r.asset), asset: r.asset }) }}
      </p>

      <!-- «Разрешить»: the amount (only less), the master address that pays, then the step-up. -->
      <form v-if="approving && buttons.approve" class="cb__form" data-testid="money-request-approve-form"
            @submit.prevent="approve">
        <label class="cb__label" for="mr-amount">{{ t('cabinet.money.approveAmount', { asset: r.asset }) }}</label>
        <input id="mr-amount" :value="amountText" class="cb__input num" inputmode="decimal" autocomplete="off"
               :placeholder="token(r.amount, r.asset)" :disabled="disabled" data-testid="money-request-approve-amount"
               @input="onAmount">
        <p v-if="amountCheck.ok === false" class="cb__err" data-testid="money-request-amount-error">
          {{ t(amountErrorKey(amountCheck.reason)) }}
        </p>
        <template v-if="sources.length">
          <span class="cb__label">{{ t('cabinet.money.payFrom') }}</span>
          <div class="cb__chips" role="radiogroup" :aria-label="t('cabinet.money.payFrom')">
            <button v-for="a in sources" :key="a.id" type="button" class="cb__chip" role="radio"
                    :aria-checked="a.id === sourceId" :class="{ 'cb__chip--on': a.id === sourceId }"
                    :data-testid="`money-request-source-${a.network}`" @click="sourceId = a.id">
              {{ pairLabel(a) }} · {{ token(a.available, a.asset) }}
            </button>
          </div>
        </template>
        <p v-else-if="sourcesLoaded" class="cb__hint" data-testid="money-request-no-source">{{ t('cabinet.money.noSource', { asset: r.asset }) }}</p>
        <button type="submit" class="cb__primary" :disabled="disabled || !canConfirm" data-testid="money-request-confirm">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4L19 6" /></svg>
          {{ t('cabinet.money.approveConfirm', { amount: amountCheck.ok ? token(amountCheck.amount, r.asset) : token(r.amount, r.asset), asset: r.asset }) }}
        </button>
        <button type="button" class="cb__secondary cb__secondary--wide" :disabled="disabled" @click="approving = false">
          {{ t('common.cancel') }}
        </button>
      </form>

      <template v-else>
        <button v-if="buttons.approve" type="button" class="cb__primary" :disabled="disabled || plan.kind === 'none'"
                data-testid="money-request-approve" :data-plan="plan.kind" @click="openApprove">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4L19 6" /></svg>
          {{ approveLabel }}
        </button>
        <button v-if="buttons.approve && plan.kind === 'none'" type="button" class="cb__secondary cb__secondary--wide"
                data-testid="money-request-topup-master" @click="router.push({ name: 'master-topup' })">
          {{ t('cabinet.money.topUpMaster') }}
        </button>
        <button v-if="buttons.decline" type="button" class="cb__danger" :disabled="disabled"
                data-testid="money-request-decline" @click="decline">
          {{ t('cabinet.money.decline') }}
        </button>
        <button v-if="buttons.cancel" type="button" class="cb__danger" :disabled="disabled"
                data-testid="money-request-cancel" @click="cancel">
          {{ t('cabinet.money.cancel') }}
        </button>
      </template>

      <button v-if="buttons.mute && mutesLoaded" type="button" class="mr__link" :disabled="disabled"
              data-testid="money-request-mute" @click="mute">
        {{ t('cabinet.money.mute', { name: other.name ?? t('cabinet.money.someone') }) }}
      </button>
      <button v-else-if="buttons.unmute" type="button" class="mr__link" :disabled="disabled"
              data-testid="money-request-unmute" @click="unmute">
        {{ t('cabinet.money.unmute', { name: other.name ?? t('cabinet.money.someone') }) }}
      </button>
    </template>
    <p v-else class="cb__text">{{ t('cabinet.money.loading') }}</p>
    <p v-if="error" class="cb__err" data-testid="chat-block-error">{{ error }}</p>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { masterAccountApi, type MasterAddress } from '@/api/masterAccount';
import { moneyRequestsApi } from '@/api/moneyRequests';
import type { StepUpFactor } from '@/api/stepup';
import type { BlockState } from '@/api/support';
import DsStatusChip from '@/components/ds/DsStatusChip.vue';
import { useStepUp } from '@/composables/useStepUp';
import { formatAmountInput } from '@/utils/money';
import { AMOUNT_SCALE, pairLabel, stepUpFactors } from '@/views/cabinet/master/masterUi';
import {
  approvalAmount, approvalPlan, approveSources, counterpart, isStaleRequestRefusal, requestButtons,
  requestFailureReasonKey, requestStateKey, requestTitle, requestTone, type ApprovalAmount, type Line,
} from '@/views/cabinet/members/memberUi';
import { useCardAction, useCardFormat } from './useCardAction';

const props = defineProps<{
  state?: Extract<BlockState, { type: 'MONEY_REQUEST' }>;
  busy?: boolean;
  reread: () => Promise<void> | void;
}>();

const { t, locale } = useI18n();
const router = useRouter();
const { token, dateTime } = useCardFormat();
const { runStepUp } = useStepUp();
const { running, error, run } = useCardAction(() => props.reread(), isStaleRequestRefusal);

const r = computed(() => props.state?.request ?? null);
const other = computed(() => (r.value ? counterpart(r.value) : { id: '', name: null }));
const title = computed<Line | null>(() => (r.value ? requestTitle(r.value, t('cabinet.money.someone')) : null));
const closed = computed(() => !!r.value && ['DECLINED', 'EXPIRED', 'CANCELLED'].includes(r.value.state));
const partial = computed(() => !!r.value && r.value.approvedAmount !== null
  && Number(r.value.approvedAmount) < Number(r.value.amount));
const cardText = computed(() => {
  const c = r.value?.card;
  if (!c) return '';
  const name = c.displayName?.trim() || t('cabinet.money.card');
  return c.lastFour ? `${name} ·· ${c.lastFour}` : name;
});

/** Mutes are the payer's own list — read once, so the card offers «Не получать» or «Снова получать». */
const muted = ref(false);
const mutesLoaded = ref(false);
const buttons = computed(() => (r.value
  ? requestButtons(r.value, muted.value)
  : { approve: false, decline: false, cancel: false, mute: false, unmute: false }));
const disabled = computed(() => !!props.busy || running.value);
const failureReasonKey = computed(() => (r.value ? requestFailureReasonKey(r.value) : null));

function lineText(l: Line): string {
  return t(l.key, l.params ?? {});
}

// ─── «Разрешить» ───
const approving = ref(false);
const amountText = ref('');
const sources = ref<MasterAddress[]>([]);
const sourcesLoaded = ref(false);
/** The read failed (unreachable): «Разрешить» stays plain and the form says what it can, as before. */
const sourcesFailed = ref(false);
const sourceId = ref<string | null>(null);
const source = computed(() => sources.value.find(a => a.id === sourceId.value) ?? null);
/** The server's «to card» minimum (the shared-balance view's), null when it gave none. */
const minimum = ref<string | number | null>(null);
/** Never more than the chosen address holds (§4.3 item 3) — refused here, not only by the server's 409. */
const amountCheck = computed(() => approvalAmount(amountText.value, r.value?.amount ?? 0, source.value?.available));
const canConfirm = computed(() => amountCheck.value.ok && !!source.value);
/** What «Разрешить» can offer against the richest address (the server's figures), once they are read. */
const plan = computed(() => (r.value && sourcesLoaded.value && !sourcesFailed.value
  ? approvalPlan(r.value.amount, sources.value[0]?.available ?? 0, minimum.value)
  : { kind: 'unknown' as const }));
const approveLabel = computed(() => {
  const req = r.value;
  if (!req || plan.value.kind !== 'partial') return t('cabinet.money.approve');
  return t('cabinet.money.approvePartial', { amount: token(plan.value.amount, req.asset),
                                             requested: token(req.amount, req.asset), asset: req.asset });
});

function amountErrorKey(reason: Extract<ApprovalAmount, { ok: false }>['reason']): string {
  if (reason === 'above') return 'vitrine.error.MONEY_REQUEST_AMOUNT_ABOVE';
  if (reason === 'insufficient') return 'vitrine.error.MASTER_INSUFFICIENT';
  return 'vitrine.error.MONEY_REQUEST_AMOUNT_INVALID';
}

function onAmount(e: Event): void {
  const el = e.target as HTMLInputElement;
  amountText.value = formatAmountInput(el.value, AMOUNT_SCALE, locale.value);
  if (el.value !== amountText.value) el.value = amountText.value;
}

/** The payer's master addresses in the request's coin and the «to card» minimum — read once, from the server. */
async function loadSources(): Promise<void> {
  if (sourcesLoaded.value || !r.value) return;
  try {
    const view = await masterAccountApi.view();
    sources.value = approveSources(view.addresses, r.value.asset);
    minimum.value = view.toCardMinimum ?? null;
    sourceId.value = sources.value[0]?.id ?? null;
  } catch {
    sources.value = [];            // the shared balance is off or unreachable — «Сначала пополните общий баланс»
    sourcesFailed.value = true;
  } finally {
    sourcesLoaded.value = true;
  }
}

async function openApprove(): Promise<void> {
  approving.value = true;
  amountText.value = '';
  await loadSources();
  // Less on the balance than asked: the form starts at what is there («Разрешить 180 USDT»), not at the request.
  const p = plan.value;
  if (p.kind === 'partial') amountText.value = formatAmountInput(p.amount, AMOUNT_SCALE, locale.value);
}

/** Approve; the first answer is 403 STEP_UP_REQUIRED (after every other check) — then the chooser and the grant. */
function approve(): void {
  const req = r.value;
  const s = source.value;
  const a = amountCheck.value;
  if (!req || !s || !a.ok) return;
  const body = { amount: a.amount, network: s.network };
  void run(async () => {
    try {
      await moneyRequestsApi.approve(req.id, body);
    } catch (e) {
      const factors = stepUpFactors<StepUpFactor>(e);
      if (!factors) throw e;
      let grant: string;
      try {
        grant = await runStepUp('MEMBER_TOP_UP', factors);
      } catch (cancelled) {
        if ((cancelled as Error)?.name === 'StepUpCancelled') return;      // dismissed — the request stays PENDING
        throw cancelled;
      }
      await moneyRequestsApi.approve(req.id, body, grant);
    }
    approving.value = false;
  });
}

function decline(): void {
  const req = r.value;
  if (req) void run(() => moneyRequestsApi.decline(req.id));
}

function cancel(): void {
  const req = r.value;
  if (req) void run(() => moneyRequestsApi.cancel(req.id));
}

function mute(): void {
  const id = other.value.id;
  if (id) void run(async () => { await moneyRequestsApi.mute(id); muted.value = true; });
}

function unmute(): void {
  const id = other.value.id;
  if (id) void run(async () => { await moneyRequestsApi.unmute(id); muted.value = false; });
}

onMounted(async () => {
  if (r.value?.side !== 'PAYER') return;
  // «Разрешить» names what the shared balance can cover before it is pressed (§4.3 item 3).
  if (r.value.canApprove) void loadSources();
  try {
    muted.value = (await moneyRequestsApi.mutes()).includes(r.value.requester.customerId);
  } catch { /* unknown — «Не получать» is still offered; the call is idempotent */ }
  mutesLoaded.value = true;
});
</script>

<style scoped lang="scss">
@use './cards.scss';

.mr__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 10px; }
.mr__title { font-family: var(--font-heading); font-size: 19px; line-height: 1.25; color: var(--onyx); overflow-wrap: anywhere; }
.mr__amount {
  margin-top: 8px; font-family: var(--font-heading); font-size: 30px; line-height: 1.1; color: var(--onyx);
  small { font-size: 14px; color: var(--muted); }
}
.mr__reason { margin: 4px 0 0; font-size: 12px; line-height: 1.5; color: var(--text-2); }
.mr__note { margin: 8px 0 0; font-size: 13px; line-height: 1.5; color: var(--text-2); overflow-wrap: anywhere; }
.mr__link {
  align-self: flex-start; margin-top: 10px; padding: 4px 0; border: 0; background: none; cursor: pointer;
  font: inherit; font-size: 11.5px; color: var(--muted); text-decoration: underline;
  &:disabled { opacity: 0.55; cursor: default; }
}
</style>
